import path from 'path';
import {LOTTIE_PLACEHOLDER} from '@packages/systems/core/utils/LottieUtils';
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import {
  AddPanelLottie64Icon,
  AddPanelSpline64Icon,
  AddPanelRive64Icon,
  ElementSplineIcon,
  ElementRiveIcon,
  ElementAnimationIcon,
} from '@packages/systems/spring';
import {Plugins, ElementPreset, Component} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';

import Animation from './Animation';
import {path as lottiePath} from './LottieSettings/path';
import {path as rivePath} from './RiveSettings/path';
import {duration} from './LottieSettings/duration';
import {loop} from './LottieSettings/loop';
import {playInReverse} from './LottieSettings/playInReverse';
import {lottieType} from './LottieSettings/lottie';
import {riveType, RIVE_PLACEHOLDER} from './RiveSettings/rive';
import Spline, {SplineSettingsType} from './Spline';
import Rive from './Rive';

import {artboard} from './RiveSettings/artboard';
import {stateMachine} from './RiveSettings/stateMachine';
import {autoPlay} from './RiveSettings/autoplay';
import {isTouchScrollEnabled} from './RiveSettings/isTouchScrollEnabled';
import {fit} from './RiveSettings/fit';
import {alignment} from './RiveSettings/alignment';
import {automaticallyHandleEvents} from './RiveSettings/automaticallyHandleEvents';

import {
  EElement,
  EText,
  EEnum,
  ERecord,
  EUnion,
  ELiteral,
} from '@packages/systems/wfdl-ast';

const LottieAddPanelIcon = wrapAddPanelIcon(AddPanelLottie64Icon);
const SplineAddPanelIcon = wrapAddPanelIcon(AddPanelSpline64Icon);
const RiveAddPanelIcon = wrapAddPanelIcon(AddPanelRive64Icon);

export const plugin = Plugins.PrivilegedPlugin({
  atoms: {Animation, Spline, Rive},
  icons: {
    Animation: ElementAnimationIcon,
    Spline: ElementSplineIcon,
    Rive: ElementRiveIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelAnimation: LottieAddPanelIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelSpline: SplineAddPanelIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelRive: RiveAddPanelIcon,
  },
  elementPresets: {
    Animation: ElementPreset.create({
      factory: ({getAsset}) => {
        if (getAsset === undefined) {
          throw Error(
            'getAsset is required to instantiate Animation elementPreset'
          );
        }
        const placeholder = getAsset('Animation', LOTTIE_PLACEHOLDER.assetName);
        return EElement({
          id: 'Animation',
          type: qualify(Plugins.name('Animation'), Component.name('Animation')),
          data: ERecord({
            tag: EText('div'),
            animation: EUnion({
              tag: 'lottie',
              value: ERecord({
                path: ELiteral({
                  name: qualify(
                    Plugins.name('Animation'),
                    typeName('LottiePath')
                  ),
                  value: LOTTIE_PLACEHOLDER.getPlaceholderData(placeholder.url),
                }),
                renderMode: EEnum('svg'),
                duration: ELiteral({
                  name: qualify(
                    Plugins.name('Animation'),
                    typeName('AnimationDuration')
                  ),
                  value: null,
                }),
                loop: ELiteral({
                  name: qualify(
                    Plugins.name('Animation'),
                    typeName('AnimationLoop')
                  ),
                  value: false,
                }),
                playInReverse: ELiteral({
                  name: qualify(
                    Plugins.name('Animation'),
                    typeName('AnimationPlayInReverse')
                  ),
                  value: false,
                }),
                loading: EEnum('lazy'),
                placeholderSrc: EText(''),
              }),
            }),
          }),
        });
      },
      category: 'Media',
      helpData: {
        description: [
          'Lottie is a file type for animations. Use this tool to upload a Lottie file, control playback, and connect it with interactions like click, hover, scroll position, and more.',
        ],
        tip: 'Turn off Autoplay to make this function accessible.',
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/lottie.57d2d7d2d6.mp4',
      },
      icon: {
        small: qualify(Plugins.name('Animation'), iconName('Animation')),
        medium: qualify(
          Plugins.name('Animation'),
          iconName('AddPanelAnimation')
        ),
      },
      label: 'Lottie Animation',
    }),
    Spline: ElementPreset.create({
      factory: () => {
        return EElement({
          id: 'Spline',
          type: qualify(Plugins.name('Animation'), Component.name('Spline')),
          data: ERecord({
            tag: EText('canvas'),
            animation: ERecord({
              type: EText('spline'),
              url: EText(''),
            }),
          }),
        });
      },
      category: 'Media',
      icon: {
        small: qualify(Plugins.name('Animation'), iconName('Spline')),
        medium: qualify(Plugins.name('Animation'), iconName('AddPanelSpline')),
      },
      helpData: {
        description: [
          'Spline is a tool for designing interactive 3D scenes. The Spline integration lets you paste your Spline scene and its events directly into Webflow. You can also animate your Spline objects in Webflow with Webflow’s native interactions.',
        ],
        link: {
          text: 'Learn more here.',
          href: 'https://university.webflow.com/lesson/spline?topics=layout-design',
        },
      },
      label: 'Spline Scene',
    }),
    Rive: ElementPreset.create({
      factory: ({getAsset}) => {
        if (getAsset === undefined) {
          throw Error(
            'getAsset is required to instantiate Animation elementPreset'
          );
        }

        const placeholder = getAsset('Animation', RIVE_PLACEHOLDER.assetName);
        return EElement({
          id: 'Rive',
          type: qualify(Plugins.name('Animation'), Component.name('Rive')),
          data: ERecord({
            animation: ERecord({
              path: ELiteral({
                name: qualify(Plugins.name('Animation'), typeName('RivePath')),
                value: RIVE_PLACEHOLDER.getPlaceholderData(placeholder.url),
              }),
              artboard: ELiteral({
                name: qualify(
                  Plugins.name('Animation'),
                  typeName('RiveArtboard')
                ),
                value: 'Truck',
              }),
              stateMachine: ELiteral({
                name: qualify(
                  Plugins.name('Animation'),
                  typeName('RiveStateMachine')
                ),
                value: 'bumpy',
              }),
              fit: ELiteral({
                name: qualify(Plugins.name('Animation'), typeName('RiveFit')),
                value: 'contain',
              }),
              alignment: ELiteral({
                name: qualify(
                  Plugins.name('Animation'),
                  typeName('RiveAlignment')
                ),
                value: 'center',
              }),
              autoPlay: ELiteral({
                name: qualify(
                  Plugins.name('Animation'),
                  typeName('RiveAutoPlay')
                ),
                value: true,
              }),
              isTouchScrollEnabled: ELiteral({
                name: qualify(
                  Plugins.name('Animation'),
                  typeName('RiveIsTouchScrollEnabled')
                ),
                value: false,
              }),
              automaticallyHandleEvents: ELiteral({
                name: qualify(
                  Plugins.name('Animation'),
                  typeName('RiveAutomaticallyHandleEvents')
                ),
                value: false,
              }),
            }),
          }),
        });
      },
      category: 'Media',
      icon: {
        small: qualify(Plugins.name('Animation'), iconName('Rive')),
        medium: qualify(Plugins.name('Animation'), iconName('AddPanelRive')),
      },
      helpData: {
        description: [
          'Rive is a file type for animations. Use this tool to upload a Rive file, control playback, and connect it with interactions like click, hover, scroll position, and more.',
        ],
        // todo: https://webflow.atlassian.net/browse/GROW-3446
        // link: {
        //   text: 'Learn more here.',
        //   href: 'https://university.webflow.com/lesson/spline?topics=layout-design',
        // },
      },
      label: 'Rive',
    }),
  },

  dataTypes: {
    LottiePath: lottiePath,
    AnimationDuration: duration,
    AnimationLoop: loop,
    AnimationPlayInReverse: playInReverse,
    Lottie: lottieType,

    SplineSettings: SplineSettingsType,

    RiveArtboard: artboard,
    RiveStateMachine: stateMachine,
    RiveFit: fit,
    RiveAlignment: alignment,
    RiveAutoPlay: autoPlay,
    RiveIsTouchScrollEnabled: isTouchScrollEnabled,
    RiveAutomaticallyHandleEvents: automaticallyHandleEvents,
    RivePath: rivePath,
    Rive: riveType,
  },

  siteModules: [
    {
      filePaths: [path.resolve(__dirname, 'webflow-lottie.js')],
      bundleIf: {
        type: 'node',
        test: ({data = {}}) =>
          (data.animation && data.animation.type === 'lottie') ||
          Boolean(
            data.animation &&
            data.animation.val &&
            data.animation.val.path &&
            data.animation.val.path.src
          ),
      },
    },
    {
      filePaths: [path.resolve(__dirname, 'webflow-spline.js')],
      bundleIf: {
        type: 'node',
        test: ({data = {}}) =>
          data.animation && data.animation.type === 'spline',
      },
    },
    {
      filePaths: [path.resolve(__dirname, 'webflow-rive.js')],
      bundleIf: {
        type: 'node',
        test: ({data = {}}) => {
          return (
            (data.animation && data.animation.type === 'Rive') ||
            Boolean(
              data.animation && data.animation.path && data.animation.path.src
            )
          );
        },
      },
    },
  ],
});
